import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useAuthPrompt } from '../App';
import { 
  getTransactions, 
  scanIncomeDocument,
  createManualEntry,
  getExpenseCategories,
  getPlaidLinkToken,
  exchangePlaidToken,
  getPlaidStatus,
  syncPlaidTransactions
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  DollarSign,
  Upload,
  Plus,
  FileText,
  Loader2,
  CheckCircle,
  Calendar,
  ArrowUpRight,
  TrendingUp,
  Briefcase,
  Users,
  RefreshCw,
  Building2,
  Link2,
  CreditCard,
  Landmark,
  AlertCircle,
  Tag,
  Wallet,
  XCircle
} from 'lucide-react';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(Math.abs(amount || 0));
}

export default function IncomeManage({ context }) {
  const auth = useAuth();
  const authPrompt = useAuthPrompt();
  const location = useLocation();
  const dataContext = context || (location.pathname.startsWith('/personal') ? 'personal' : 'business');
  
  const [activeTab, setActiveTab] = useState('link');
  const [incomeSources, setIncomeSources] = useState([]);
  const [recentIncome, setRecentIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalIncome, setTotalIncome] = useState(0);

  useEffect(function loadData() {
    Promise.all([
      getExpenseCategories(),
      getTransactions({ limit: 50 })
    ]).then(function(results) {
      setIncomeSources(results[0].data.income_sources || []);
      const income = (results[1].data.transactions || []).filter(function(t) { return t.amount > 0; });
      setRecentIncome(income);
      setTotalIncome(income.reduce(function(sum, t) { return sum + t.amount; }, 0));
    }).catch(function(err) {
      console.error('Failed to load data:', err);
    }).finally(function() {
      setLoading(false);
    });
  }, []);

  function requireAuth(action) {
    if (!auth.isAuthenticated) {
      authPrompt.showAuthPrompt(action);
      return false;
    }
    return true;
  }

  const tabClass = 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all';
  const activeClass = tabClass + ' bg-green-500 text-white';
  const inactiveClass = tabClass + ' bg-muted text-muted-foreground hover:text-foreground';

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="income-manage">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
            {dataContext === 'personal' ? 'Personal' : 'Business'} Income
          </h1>
          <p className="text-muted-foreground mt-1">Link your bank or add income manually</p>
        </div>
        
        <Card className="card-premium px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-500/10">
              <DollarSign className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Income (This Month)</p>
              <p className="text-2xl font-bold text-green-500 font-['Space_Grotesk']">{formatCurrency(totalIncome)}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        <button onClick={function() { setActiveTab('link'); }} className={activeTab === 'link' ? activeClass : inactiveClass} data-testid="income-tab-link">
          <Building2 className="w-4 h-4" /> <span className="hidden sm:inline">Link Bank</span>
        </button>
        <button onClick={function() { setActiveTab('manual'); }} className={activeTab === 'manual' ? activeClass : inactiveClass} data-testid="income-tab-manual">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Income</span>
        </button>
        <button onClick={function() { setActiveTab('scan'); }} className={activeTab === 'scan' ? activeClass : inactiveClass} data-testid="income-tab-scan">
          <Upload className="w-4 h-4" /> <span className="hidden sm:inline">Scan Document</span>
        </button>
        <button onClick={function() { setActiveTab('history'); }} className={activeTab === 'history' ? activeClass : inactiveClass} data-testid="income-tab-history">
          <TrendingUp className="w-4 h-4" /> <span className="hidden sm:inline">Income History</span>
        </button>
      </div>

      {activeTab === 'link' && <IncomeBankLinkTab requireAuth={requireAuth} dataContext={dataContext} />}
      {activeTab === 'manual' && <ManualIncomeTab requireAuth={requireAuth} sources={incomeSources} />}
      {activeTab === 'scan' && <ScanIncomeTab requireAuth={requireAuth} />}
      {activeTab === 'history' && <IncomeHistoryTab income={recentIncome} loading={loading} />}
    </div>
  );
}

function IncomeBankLinkTab({ requireAuth, dataContext }) {
  const [plaidStatus, setPlaidStatus] = useState({ connected: false, items_count: 0, accounts: [] });
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(function fetchStatus() {
    getPlaidStatus().then(function(res) {
      setPlaidStatus(res.data);
    }).catch(function(err) {
      console.error('Failed to fetch Plaid status:', err);
    }).finally(function() {
      setLoading(false);
    });
  }, []);

  function handleLinkBank() {
    if (!requireAuth('Link Bank Account')) return;
    setLinking(true);
    
    getPlaidLinkToken().then(function(tokenRes) {
      const linkToken = tokenRes.data.link_token;
      if (!linkToken) {
        toast.error('Failed to initialize bank linking. Please ensure Plaid API keys are configured.');
        setLinking(false);
        return;
      }
      
      if (window.Plaid) {
        const handler = window.Plaid.create({
          token: linkToken,
          onSuccess: function(publicToken, metadata) {
            exchangePlaidToken(publicToken, metadata.institution?.name).then(function() {
              toast.success('Bank account linked successfully!');
              getPlaidStatus().then(function(res) { setPlaidStatus(res.data); });
            }).catch(function() {
              toast.error('Failed to link bank account');
            });
          },
          onExit: function(err) {
            if (err) toast.error('Bank linking cancelled');
            setLinking(false);
          }
        });
        handler.open();
      } else {
        toast.error('Plaid SDK not loaded.');
        setLinking(false);
      }
    }).catch(function() {
      toast.error('Failed to start bank linking.');
      setLinking(false);
    });
  }

  function handleSync() {
    if (!requireAuth('Sync Transactions')) return;
    setSyncing(true);
    syncPlaidTransactions().then(function(res) {
      toast.success('Synced ' + (res.data.transactions_added || 0) + ' transactions!');
    }).catch(function() {
      toast.error('Failed to sync transactions');
    }).finally(function() {
      setSyncing(false);
    });
  }

  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-2">Loading bank connection status...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="card-premium overflow-hidden">
        <CardHeader className="border-b border-border bg-green-500/5">
          <CardTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-green-500" />
            Bank Account Connection
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your bank to automatically import {dataContext} income deposits
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {plaidStatus.connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-emerald-500/20">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-400">Bank Connected</p>
                    <p className="text-sm text-muted-foreground">{plaidStatus.items_count} account(s) linked</p>
                  </div>
                </div>
                <Button variant="outline" onClick={handleSync} disabled={syncing} className="border-emerald-500/30 hover:bg-emerald-500/10">
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sync Income
                </Button>
              </div>
              <Button variant="outline" onClick={handleLinkBank} disabled={linking} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Link Another Account
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Bank Account Connected</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Link your bank account to automatically import income deposits and track your earnings.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 text-green-500" />
                  <p className="font-medium text-sm">Auto-Import</p>
                  <p className="text-xs text-muted-foreground">Detect income deposits</p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                  <Tag className="w-6 h-6 mx-auto mb-2 text-green-500" />
                  <p className="font-medium text-sm">Smart Sorting</p>
                  <p className="text-xs text-muted-foreground">Categorize by source</p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-500" />
                  <p className="font-medium text-sm">Track Trends</p>
                  <p className="text-xs text-muted-foreground">Monitor income growth</p>
                </div>
              </div>
              <Button onClick={handleLinkBank} disabled={linking} className="w-full h-12 text-base font-semibold bg-green-500 hover:bg-green-600 text-white" data-testid="link-income-bank-button">
                {linking ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connecting...</> : <><Link2 className="w-5 h-5 mr-2" /> Link Bank Account</>}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  Bank-level encryption • Read-only access • Plaid secured
                </span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {!plaidStatus.connected && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-500">Plaid API Key Required</p>
                <p className="text-sm text-muted-foreground mt-1">
                  To enable automatic bank linking, provide your Plaid API credentials.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ManualIncomeTab({ requireAuth, sources }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (!requireAuth('Add Income')) return;
    if (!amount || !description) { toast.error('Please fill in required fields'); return; }
    
    setSaving(true);
    createManualEntry({
      amount: Math.abs(parseFloat(amount)),
      description: description,
      category: source || 'Income',
      date: date,
      type: 'income'
    }).then(function() {
      toast.success('Income added!');
      setAmount(''); setDescription(''); setSource('');
    }).catch(function() {
      toast.error('Failed to add income');
    }).finally(function() {
      setSaving(false);
    });
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="w-5 h-5 text-green-500" />
          Add Income
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="income-amount">Amount *</Label>
              <Input id="income-amount" type="number" step="0.01" placeholder="0.00" value={amount} onChange={function(e) { setAmount(e.target.value); }} required data-testid="income-amount" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="income-date">Date</Label>
              <Input id="income-date" type="date" value={date} onChange={function(e) { setDate(e.target.value); }} data-testid="income-date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="income-description">Description *</Label>
            <Input id="income-description" placeholder="e.g., Client Payment, Freelance Work" value={description} onChange={function(e) { setDescription(e.target.value); }} required data-testid="income-description" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="income-source">Source</Label>
            <Input id="income-source" placeholder="e.g., Freelance, Salary, Investment" value={source} onChange={function(e) { setSource(e.target.value); }} data-testid="income-source" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-green-500 hover:bg-green-600 text-white">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Add Income</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ScanIncomeTab({ requireAuth }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  function handleFileSelect(e) {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      const reader = new FileReader();
      reader.onload = function(ev) { setPreview(ev.target && ev.target.result); };
      reader.readAsDataURL(selectedFile);
    }
  }

  function handleScan() {
    if (!requireAuth('Scan Income Document')) return;
    if (!file) { toast.error('Please select a file first'); return; }
    
    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);
    
    scanIncomeDocument(formData).then(function(res) {
      setResult(res.data);
      toast.success('Document scanned successfully!');
    }).catch(function() {
      toast.error('Failed to scan document');
    }).finally(function() {
      setScanning(false);
    });
  }

  function clearFile() {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-green-500" />
          Scan Income Document
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-green-500/50 transition-colors cursor-pointer" onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}>
          <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFileSelect} className="hidden" data-testid="income-file-input" />
          {preview ? (
            <div className="space-y-4">
              <img src={preview} alt="Document preview" className="max-h-48 mx-auto rounded-lg" />
              <p className="text-sm text-muted-foreground">{file && file.name}</p>
              <Button variant="ghost" size="sm" onClick={function(e) { e.stopPropagation(); clearFile(); }}>
                <XCircle className="w-4 h-4 mr-2" /> Clear
              </Button>
            </div>
          ) : (
            <div>
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">Click to upload document</p>
              <p className="text-sm text-muted-foreground">Invoice, pay stub, or 1099</p>
            </div>
          )}
        </div>

        <Button onClick={handleScan} disabled={scanning || !file} className="w-full bg-green-500 hover:bg-green-600 text-white">
          {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : <><FileText className="w-4 h-4 mr-2" /> Scan Document</>}
        </Button>

        {result && (
          <Card className="bg-green-500/5 border-green-500/30">
            <CardHeader><CardTitle className="text-base">Extracted Data</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Payer:</span><span className="font-medium">{result.payer || result.vendor || 'Unknown'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Amount:</span><span className="font-medium text-green-500">{formatCurrency(result.amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Date:</span><span className="font-medium">{result.date || 'Unknown'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><Badge variant="outline" className="text-green-500 border-green-500/30">{result.income_type || 'Income'}</Badge></div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}

function IncomeHistoryTab({ income, loading }) {
  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-2">Loading income history...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          Income History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {income.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No income recorded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {income.slice(0, 20).map(function(txn, i) {
              return (
                <div key={txn.id || i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-3">
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="font-medium">{txn.description || txn.vendor || 'Income'}</p>
                      <p className="text-xs text-muted-foreground">{txn.date} • {txn.category || 'Income'}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-green-500">{formatCurrency(txn.amount)}</p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
